import { Component, inject, Input } from '@angular/core';
import {
  AbstractComponent,
  BrowserTools,
  NavigationService,
  SolFieldType,
  SolSelectComponent,
  SolTopbarComponent,
} from '@sol/components';
import { SessionService } from '@asm/components-platform-context/services/session.service';
import { AsmNavbarTools } from '@asm/components-platform-context/tools/asm-navbar-tools';
import { AsmRouteId } from '@asm/components-core/models/asm-route-id';
import { AsmTopbarState } from './asm-topbar.state';

@Component({
  selector: 'asm-topbar',
  imports: [SolTopbarComponent, SolSelectComponent],
  templateUrl: './asm-topbar.component.html',
})
export class AsmTopbarComponent extends AbstractComponent<AsmTopbarState> {
  protected readonly SolFieldType = SolFieldType;

  // Inputs
  @Input()
  public set hideBreadcrumbs(value: boolean) {
    this.update(state => state.withHideBreadcrumbs(value ?? false));
  }

  // Dependencies
  private readonly sessionService = inject(SessionService);
  private readonly navigationService = inject(NavigationService);

  // Computed
  protected readonly loading$ = this.compute(state => state.loading);
  protected readonly partnerVisible$ = this.compute(state => state.partnerVisible);
  protected readonly partner$ = this.compute(state => state.partner);
  protected readonly partners$ = this.compute(state => state.partners);
  protected readonly scopeVisible$ = this.compute(state => state.scopeVisible);
  protected readonly scope$ = this.compute(state => state.scope);
  protected readonly scopes$ = this.compute(state => state.scopes);
  protected readonly subscope$ = this.compute(state => state.subscope);
  protected readonly subscopes$ = this.compute(state => state.subscopes);
  protected readonly isEmbedded$ = this.compute(state => state.isEmbedded);
  /** Derived in state — replaces compound template expression (M-4 fix). */
  protected readonly showCustomBreadcrumbs$ = this.compute(state => state.showCustomBreadcrumbs);
  protected readonly showScopeSwitcher$ = this.compute(state => state.showScopeSwitcher);
  protected readonly showPartnerSwitcher$ = this.compute(state => state.showPartnerSwitcher);

  constructor() {
    super(AsmTopbarState.of(BrowserTools.isEmbedded()));

    // Watch (sub)scope info
    this.sessionService.watch(sessionState => {
      const currentScope = sessionState.scope?.scope;
      const filteredSubscopes = currentScope ? sessionState.subscopes.filter(s => s.scopeName === currentScope) : [];
      this.update(state =>
        state.withSessionData(
          !sessionState.fetched,
          sessionState.currentPartnerId,
          AsmNavbarTools.toPartnerSelectValues(sessionState.partners),
          currentScope,
          sessionState.scopes,
          sessionState.scope?.subscopeId,
          AsmNavbarTools.toSubscopeSelectValues(filteredSubscopes),
        ),
      );
    });

    // Watch navbar info
    this.navigationService.watchComputed(
      nss => nss.appId,
      appId => {
        const partnerVisible = appId === AsmRouteId.app_partner;
        const scopeVisible = appId === AsmRouteId.app_platform;
        this.update(state => state.withNavigation(partnerVisible, scopeVisible));
      },
    );
  }

  protected onScopeChange(scopeId: string | undefined) {
    if (scopeId === undefined) {
      return;
    }
    this.sessionService.updateCurrentScope(scopeId);
  }

  protected onSubscopeChange(subscopeId: string | undefined) {
    this.sessionService.updateCurrentSubscope(subscopeId);
  }

  protected onPartnerChange(partnerId: string | undefined) {
    if (partnerId === undefined) {
      return;
    }
    this.sessionService.updateCurrentPartner(partnerId);
  }
}
